import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { LoggerService } from '../services/LoggerService';

export interface User {
  id: string;
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role: string;
  is_active: boolean;
  is_verified: boolean;
  last_login?: string;
  created_at: string;
  updated_at: string;
}

export interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  familyId: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  tokenExpiry: number | null;
  refreshTimer: ReturnType<typeof setTimeout> | null;
}

export interface AuthActions {
  setUser: (user: User) => void;
  setTokens: (accessToken: string, refreshToken: string, familyId: string, expiresIn: number) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  login: (accessToken: string, refreshToken: string, familyId: string, expiresIn: number, user?: User) => void;
  logout: () => void;
  clearError: () => void;
  refreshAccessToken: () => Promise<boolean>;
  startTokenRefreshTimer: () => void;
  stopTokenRefreshTimer: () => void;
  initializeAuth: () => void;
}

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set, get) => ({
      // State
      user: null,
      accessToken: null,
      refreshToken: null,
      familyId: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      tokenExpiry: null,
      refreshTimer: null,

      // Actions
      setUser: (user: User) => {
        set({ user, isAuthenticated: true });
      },

      setTokens: (accessToken: string, refreshToken: string, familyId: string, expiresIn: number) => {
        const expiry = Date.now() + (expiresIn * 1000);
        set({ 
          accessToken, 
          refreshToken, 
          familyId, 
          tokenExpiry: expiry,
          isAuthenticated: true 
        });
        get().startTokenRefreshTimer();
      },

      setLoading: (isLoading: boolean) => {
        set({ isLoading });
      },

      setError: (error: string | null) => {
        set({ error });
      },

      login: (accessToken: string, refreshToken: string, familyId: string, expiresIn: number, user?: User) => {
        const expiry = Date.now() + (expiresIn * 1000);
        set({
          user: user || null,
          accessToken,
          refreshToken,
          familyId,
          tokenExpiry: expiry,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
        get().startTokenRefreshTimer();
      },

      logout: () => {
        get().stopTokenRefreshTimer();
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          familyId: null,
          tokenExpiry: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      },

      clearError: () => {
        set({ error: null });
      },

      refreshAccessToken: async (): Promise<boolean> => {
        const { refreshToken } = get();
        if (!refreshToken) {
          get().logout();
          return false;
        }

        try {
          const response = await fetch('/api/v1/auth/refresh', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${refreshToken}`,
            },
            credentials: 'include', // Include cookies for HttpOnly refresh token
          });

          if (!response.ok) {
            throw new Error('Token refresh failed');
          }

          const data = await response.json();
          get().setTokens(
            data.access_token,
            data.refresh_token,
            data.family_id,
            data.expires_in
          );
          return true;
        } catch (error) {
          LoggerService.error('Token refresh failed', { message: (error as Error).message });
          get().logout();
          return false;
        }
      },

      startTokenRefreshTimer: () => {
        get().stopTokenRefreshTimer(); // Clear existing timer
        
        const { tokenExpiry } = get();
        if (!tokenExpiry) return;

        // Refresh token 2 minutes before expiry
        const refreshTime = tokenExpiry - Date.now() - (2 * 60 * 1000);
        
        if (refreshTime > 0) {
          const timer = setTimeout(() => {
            get().refreshAccessToken();
          }, refreshTime);
          
          set({ refreshTimer: timer });
        }
      },

      stopTokenRefreshTimer: () => {
        const { refreshTimer } = get();
        if (refreshTimer) {
          clearTimeout(refreshTimer);
          set({ refreshTimer: null });
        }
      },

      initializeAuth: async () => {
        const { accessToken, refreshToken, tokenExpiry, isAuthenticated, user } = get();

        // SECURITY: accessToken is no longer persisted (memory only)
        // On page reload, we need to refresh using the refreshToken

        // If we have a refreshToken but no accessToken, try to refresh
        if (refreshToken && !accessToken && isAuthenticated && user) {
          const success = await get().refreshAccessToken();
          if (!success) {
            get().logout();
          }
          return;
        }

        // If authenticated flag is set but missing user data, clear auth state
        if (isAuthenticated && !user) {
          get().logout();
          return;
        }

        // If we have all required data and token is not expired, start the refresh timer
        if (accessToken && tokenExpiry && isAuthenticated && user) {
          if (Date.now() < tokenExpiry) {
            get().startTokenRefreshTimer();
          } else {
            // Token is expired, try to refresh
            const success = await get().refreshAccessToken();
            if (!success) {
              get().logout();
            }
          }
        } else if (isAuthenticated && refreshToken) {
          // Has auth flag and refreshToken but no accessToken - try refresh
          const success = await get().refreshAccessToken();
          if (!success) {
            get().logout();
          }
        } else if (isAuthenticated) {
          // Has isAuthenticated flag but missing required data - clear it
          get().logout();
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        // SECURITY: Only persist non-sensitive data
        // accessToken is stored in memory only to prevent XSS token theft
        // refreshToken is sent via HttpOnly cookie by the backend
        user: state.user,
        // accessToken: EXCLUDED - memory only for XSS protection
        // refreshToken: EXCLUDED - must be sent via HttpOnly cookie only (XSS protection)
        familyId: state.familyId,
        tokenExpiry: state.tokenExpiry,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
